import {
  Box,
  Button,
  Container,
  Header,
  PieChart,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react';
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { forEach, get, values } from 'lodash';
import { getStudySummaryActions } from '../../study_summary/redux';
import STUDY_SUMMARY_TYPES from '../../study_summary/constants/studySummaries';
import QRE_SUM_AGG_TYPES from '../../study_summary/components/qre_tab/constants/qreTypes';
import { getQREClassification } from '../../../redux/reducers/studyTypeReducers';
import { DEFAULT_STUDY_TYPE } from '../../../common/config/api_constants';
import { currencyForCountry } from '../utils/jurisdiction';

// Merge aggByFederalQRE across all statuses -> { qreTypeKey: totalDollars }.
const mergeFederalQRE = qreSummary => {
  const merged = {};
  forEach(get(qreSummary, 'aggByStatus', {}), statusValue => {
    forEach(get(statusValue, 'aggByFederalQRE', {}), (amount, key) => {
      merged[key] = (merged[key] || 0) + (Number(amount) || 0);
    });
  });
  return merged;
};

/**
 * QRE overview card — REAL data via getStudySummary (summaryType=QRE), same
 * source as the Study Summary QRE tab.
 */
const QRECard = ({
  onViewDetails,
  qreSummary,
  isLoading,
  qreClassification,
  fetchSummary,
  clearQRE,
  country,
}) => {
  // Clear first so another season's QRE isn't shown while this one loads.
  useEffect(() => {
    clearQRE();
    fetchSummary({
      body: { summaryType: STUDY_SUMMARY_TYPES.QRE.key, qreType: qreClassification },
    });
  }, [fetchSummary, clearQRE, qreClassification]);

  // Currency follows the season's jurisdiction (falls back to USD) rather than
  // being hardcoded, since seasons span US/UK/CA/AU/DE/FR/IE.
  const formatMoney = useMemo(() => {
    const fmt = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyForCountry(country),
      notation: 'compact',
      maximumFractionDigits: 2,
    });
    return n => fmt.format(Number(n) || 0);
  }, [country]);

  const merged = mergeFederalQRE(qreSummary);
  const total = values(merged).reduce((a, b) => a + b, 0);

  const pieData = values(QRE_SUM_AGG_TYPES)
    .filter(t => (merged[t.key] || 0) > 0)
    .map(t => ({ title: t.label, value: merged[t.key], color: t.color }));

  let qreBody;
  if (isLoading && !total) {
    qreBody = (
      <Box textAlign="center" padding="l">
        <Spinner size="normal" /> Loading QRE…
      </Box>
    );
  } else if (pieData.length > 0) {
    qreBody = (
      <div style={{ maxWidth: 520 }}>
        <PieChart
          data={pieData}
          size="medium"
          variant="donut"
          hideFilter
          innerMetricValue={formatMoney(total)}
          innerMetricDescription="Total QRE"
          segmentDescription={(segment, sum) =>
            `${formatMoney(segment.value)} (${((segment.value / sum) * 100).toFixed(0)}%)`
          }
          ariaLabel="QRE breakdown"
        />
      </div>
    );
  } else {
    qreBody = (
      <Box color="text-body-secondary" textAlign="center" padding="l">
        No QRE data yet. Complete labor surveys to compute QREs.
      </Box>
    );
  }

  return (
    <Container
      header={
        <Header variant="h2" actions={<Button onClick={onViewDetails}>View details</Button>}>
          QRE
        </Header>
      }
    >
      <SpaceBetween size="m">{qreBody}</SpaceBetween>
    </Container>
  );
};

QRECard.propTypes = {
  onViewDetails: PropTypes.func.isRequired,
  qreSummary: PropTypes.object,
  isLoading: PropTypes.bool,
  qreClassification: PropTypes.string,
  fetchSummary: PropTypes.func.isRequired,
  clearQRE: PropTypes.func.isRequired,
  country: PropTypes.string,
};

QRECard.defaultProps = {
  qreSummary: {},
  isLoading: false,
  qreClassification: undefined,
  country: undefined,
};

const mapStateToProps = state => ({
  qreSummary: get(state, `entities.studySummary.data.${STUDY_SUMMARY_TYPES.QRE.key}`, {}) || {},
  isLoading: get(
    state,
    ['entities', 'studySummary', 'fetchingByType', STUDY_SUMMARY_TYPES.QRE.key],
    false,
  ),
  qreClassification:
    get(state, 'studyType.qreClassification') || getQREClassification(DEFAULT_STUDY_TYPE),
});

const mapDispatchToProps = dispatch => ({
  fetchSummary: payload => dispatch(getStudySummaryActions.BEGIN(payload)),
  clearQRE: () => dispatch(getStudySummaryActions.CLEAR(STUDY_SUMMARY_TYPES.QRE.key)),
});

export default connect(mapStateToProps, mapDispatchToProps)(QRECard);
